import { API } from 'components/CommonFunc/amplifyApiCompat';
import { ConsoleLogger } from 'aws-amplify/utils';
import React from "react";
import {Alert, Button, Col, Form, Row} from "react-bootstrap";
import {v4 as uuidv4} from "uuid";
import {USER_EMAIL} from "../../../Auth/LoginUser";
import {DataAudit} from "../../../CommonApi/DataAudit";
import {EventType, FrontendModule} from "../../../CommonApi/Enum";
import {UsageTracking} from "../../../CommonApi/UsageTracking";
import {ApiCallStatus} from "../../../CommonFunc/Enum";
import {gppApiConsts as gppApiConst} from "../../GuidePlasmidIndel/GppApiConsts";
import {Plasmid} from "../../GuidePlasmidIndel/PlasmidForm";
import {DeliveryMethod, View} from "../Enum";
import {genGenPlateApiConst} from "../GenGenPlateApiConst";
import {PlateRow} from "../PlateRow";
import {GenGenPlateMap} from "./GenGenPlateMapForm";
import {TanStackPlateMapTable} from "./GenGenPlateMapTable";

const logger = new ConsoleLogger("GenGenPlateMapPlasmid")

interface GenGenPlateMapPlasmidState {
    columns: any[]
    updateNote: string
    owner: string
    user: string
    plateMap: string
    plateMapSubmissionStatus: ApiCallStatus
    plateMapSubmissionMessage: string
    plateMapRetrievalStatus: ApiCallStatus
    plateMapRetrievalMessage: string
    possiblePlasmidList: Plasmid[]
    possiblePlasmidRetrievalStatus: ApiCallStatus
    possiblePlasmidRetrievalMessage: string
}

interface GenGenPlateMapPlasmidProps {
    genGenPlateMap: GenGenPlateMap
    geneAs: string
    geneBs: string
    owner: string
    view: View
    refreshToken: string
}

const DEFAULT_COLUMN_LIST = [
        {dataField: "0", text: ""},
        {dataField: "A", text: "1"},
        {dataField: "B", text: "2"},
        {dataField: "C", text: "3"},
        {dataField: "D", text: "4"},
        {dataField: "E", text: "5"},
        {dataField: "F", text: "6"},
        {dataField: "G", text: "7"},
        {dataField: "H", text: "8"},
        {dataField: "I", text: "9"},
        {dataField: "J", text: "10"},
        {dataField: "K", text: "11"},
        {dataField: "L", text: "12"},
    ]

export const DEFAULT_PLATE_MAP: string =
    `empty empty empty empty empty empty empty empty empty empty empty empty\n` +
    `PL945 PL851_for_count PL851 empty empty empty empty empty empty empty empty media_only\n` +
    `PL945 PL851_for_count PL851 as_above as_above as_above as_above as_above as_above as_above as_above media_only\n` +
    `PL945 PL851_for_count PL851 as_above as_above as_above as_above as_above as_above as_above as_above media_only\n` +
    `nv_puro PL681 PL675 empty empty empty empty empty empty empty empty nv\n` +
    `nv_puro PL681 PL675 as_above as_above as_above as_above as_above as_above as_above as_above nv\n` +
    `nv_puro PL681 PL675 as_above as_above as_above as_above as_above as_above as_above as_above nv\n` +
    `empty empty empty empty empty empty empty empty empty empty empty empty`

export class GenGenPlateMapPlasmid extends React.Component<GenGenPlateMapPlasmidProps, GenGenPlateMapPlasmidState> {
    static defaultProps = {
        genGenPlateMap: {
            plateId: "",
            deliveryMethod: DeliveryMethod.Lentivirus,
            geneAs: "", // Important for getting possible plasmid lists
            geneBs: "",
            plateMap: ""
        }
    }

    constructor(props: any) {
        super(props);
        this.state = {
            columns: DEFAULT_COLUMN_LIST,
            plateMap: "",
            plateMapSubmissionStatus: ApiCallStatus.NoData,
            plateMapSubmissionMessage: "",
            plateMapRetrievalStatus: ApiCallStatus.NoData,
            plateMapRetrievalMessage: "",
            possiblePlasmidList: [],
            possiblePlasmidRetrievalStatus: ApiCallStatus.NoData,
            possiblePlasmidRetrievalMessage: "",
            user: USER_EMAIL,
            owner: props.owner,
            updateNote: ""
        }
    }

    componentDidMount() {
        this.updateColumnsWithPossiblePlasmidLists()
    }

    componentDidUpdate(prevProps: Readonly<GenGenPlateMapPlasmidProps>, prevState: Readonly<GenGenPlateMapPlasmidState>, snapshot?: any) {
        // Update possible plasmid list when props change
        if (prevProps.refreshToken !== this.props.refreshToken ||
            prevProps.genGenPlateMap.plateId !== this.props.genGenPlateMap.plateId ||
            this.props.geneAs !== prevProps.geneAs ||
            this.props.geneBs !== prevProps.geneBs
        ) {
            this.updateColumnsWithPossiblePlasmidLists()
        }
    }

    updateColumnsWithPossiblePlasmidLists() {
        if (this.props.view === View.Read) {
            this.setState({
                plateMap: this.props.genGenPlateMap.plateMap,
                columns: DEFAULT_COLUMN_LIST,
            })
            return;
        }

        if (this.props.geneAs === "" || this.props.geneBs === "") {
            return
        }

        this.setState({
            possiblePlasmidRetrievalStatus: ApiCallStatus.Loading,
            possiblePlasmidList: [],
            possiblePlasmidRetrievalMessage: "",
        }, () => {
            API.get(
                gppApiConst.API_NAME,
                gppApiConst.plasmid.path.GENE_A_GENE_B,
                {
                    headers: {},
                    queryParams: {
                        "genea": this.props.geneAs,
                        "geneb": this.props.geneBs,
                    }},
            ).then(response => {
                logger.debug("Response from API", response)

                // Handling case where this.props.genGenPlateMap.plateMap contains no-longer-valid plasmid
                let plasmidIds: string[] = response.data.data.map((plasmid: Plasmid) => plasmid.id)
                let plateMap = PlateRow.convertStrToPlateMapRowList(this.props.genGenPlateMap.plateMap)
                for (let row of plateMap) {
                    Object.keys(row).forEach((key: string) => {
                        // @ts-ignore
                        logger.info(`row[${key}] = ${row[key]}`)
                        // @ts-ignore
                        if (row[key].startsWith("PL") && row[key] !== "PL945" &&
                            // @ts-ignore
                            row[key] !== "PL851_for_count" && !plasmidIds.includes(row[key])) {
                            // @ts-ignore
                            logger.info(`Change ${row[key]} to need_update`)
                            // @ts-ignore
                            row[key] = "need_update"
                        }
                    })
                }
                logger.info("platemap = ", plateMap)

                this.setState({
                    plateMap: this.props.view === View.Import ? DEFAULT_PLATE_MAP : PlateRow.convertPlateMapRowListToStr(plateMap),
                    possiblePlasmidRetrievalStatus: ApiCallStatus.Completed,
                    possiblePlasmidRetrievalMessage: "",
                    possiblePlasmidList: response.data,
                    columns: DEFAULT_COLUMN_LIST,
                })
            }).catch(err => {
                logger.error(err)
                this.setState({
                    possiblePlasmidList: [],
                    possiblePlasmidRetrievalStatus: ApiCallStatus.Error,
                    possiblePlasmidRetrievalMessage: err.response.data.message,
                    columns: DEFAULT_COLUMN_LIST,
                })
            })
        })
    }

    convertPlasmidListToOptions(plasmidList: any): {value: string, label: string}[] {
        // Handle both old and new response formats due to React 19 compatibility issues
        let actualPlasmidList = plasmidList
        if (plasmidList && !Array.isArray(plasmidList)) {
            if (plasmidList.data && Array.isArray(plasmidList.data)) {
                actualPlasmidList = plasmidList.data
            } else {
                console.error("Unexpected plasmidList format: ", plasmidList)
                actualPlasmidList = []
            }
        }

        let result: {value: string, label: string}[] = [
            {value: "as_above", label: "as_above"},
        ]
        for (const plasmid of actualPlasmidList) {
            result.push({value: plasmid.id, label: `${plasmid.id} - ${plasmid.name}`})
        }
        result.push(...[
            {value: "nv", label: "nv"},
            {value: "nv_puro", label: "nv_puro"},
            {value: "empty", label: "empty"},
            {value: "media_only", label: "media_only"},
            {value: "need_update", label: "need_update"},
            {value: "PL945", label: "PL945"},
            {value: "PL851_for_count", label: "PL851_for_count"},
        ])
        return result
    }

    // Refactored api call method
    async apiCall(
        options: {
            method: 'put' | 'patch',
            eventType: EventType,
            plateId: string,
            plateMap: string,
            updateNote?: string,
        }
    ) {
        const { method, eventType, plateId, plateMap, updateNote } = options

        // Form validation
        if (plateMap.includes("need_update")) {
            this.setState({
                plateMapSubmissionStatus: ApiCallStatus.Error,
                plateMapSubmissionMessage: "Change \"need_update\" to correct plasmid"
            })
            return
        }

        if (method === 'patch' && !updateNote) {
            this.setState({
                plateMapSubmissionStatus: ApiCallStatus.Error,
                plateMapSubmissionMessage: "Update note cannot be empty"
            })
            return
        }

        UsageTracking.recordEvent({
            uuid: uuidv4(),
            module: FrontendModule.DataCatalog,
            event: eventType,
            table: genGenPlateApiConst.tableName.MAP,
            database: genGenPlateApiConst.DATABASE_NAME,
            keywords: "", // Comma seperated
            timestamp: new Date()
        })

        this.setState({
            plateMapSubmissionStatus: ApiCallStatus.Loading,
            plateMapSubmissionMessage: "",
        })

        try {
            const body = method === 'put' ? { plateId, plateMap } :
                { plateId, plateMap, additionalNote: updateNote }
            const response = await API[method](
                genGenPlateApiConst.API_NAME,
                genGenPlateApiConst.path.MAP,
                { body }
            )

            DataAudit.recordEvent({
                database: genGenPlateApiConst.DATABASE_NAME,
                table: genGenPlateApiConst.tableName.MAP,
                rowId: plateId,
                updateNote: updateNote || "",
                dataObject: {
                    plateId: plateId,
                    plateMap: plateMap,
                    user: this.state.user,
                },
            })

            // Set success message
            this.setState({
                plateMapSubmissionStatus: ApiCallStatus.Completed,
                plateMapSubmissionMessage: "",
            })
        } catch (error: unknown) {
            logger.error(error)
            let errorMessage = "An unknown error occurred."
            if (error && typeof error === "object" && 'response' in error) {
                const apiError = error as { response?: { data?: { message?: string } } }
                if (apiError.response?.data?.message) {
                    errorMessage = apiError.response.data.message
                }
            } else if (error instanceof Error) {
                errorMessage = error.message
            } else if (typeof error === 'string') {
                errorMessage = error
            }

            this.setState({
                plateMapSubmissionStatus: ApiCallStatus.Error,
                plateMapSubmissionMessage: errorMessage,
            })
        }
    }

    handleUpdateNoteChange = (e: any) => {
        this.setState({
            updateNote: e.target.value
        })
    }

    handlePlateMapChange = (newPlateMap: string) => {
        this.setState({ plateMap: newPlateMap })
    }

    // Submit plate map using refactored api call
    submitPlateMap = () => {
        if (this.props.view === View.Import) {
            this.apiCall({
                method: 'put',
                eventType: EventType.Import,
                plateId: this.props.genGenPlateMap.plateId,
                plateMap: this.state.plateMap,
            })
        } else if (this.props.view === View.Update) {
            this.apiCall({
                method: 'patch',
                eventType: EventType.Update,
                plateId: this.props.genGenPlateMap.plateId,
                plateMap: this.state.plateMap,
                updateNote: this.state.updateNote,
            })
        }
    }

    render() {
        // Except for view Read, all other view should trigger updateColumnsWithPossiblePlasmidLists() which would change ApiCallStatus.NoData to something else
        if (this.state.possiblePlasmidRetrievalStatus === ApiCallStatus.NoData && this.props.view !== View.Read) {
            return <></>
        }

        if (this.state.possiblePlasmidRetrievalStatus === ApiCallStatus.Loading) {
            return <p className={"text-primary"}>Finding all plasmids of {this.props.geneAs} and {this.props.geneBs} ...</p>
        }
        if (this.state.possiblePlasmidRetrievalStatus === ApiCallStatus.Error) {
            return <Alert className={"mt-3"} variant={"danger"}>
                Error finding plasmid matching geneA = {this.props.geneAs} and/or geneB = {this.props.geneBs}
            </Alert>
        }

        if (this.state.plateMapRetrievalStatus === ApiCallStatus.Loading) {
            return <p className={"text-primary"}>Retrieving plate map ...</p>
        }
        if (this.state.plateMapRetrievalStatus === ApiCallStatus.Error) {
            return <Alert className={"mt-3"} variant={"danger"}>{this.state.plateMapRetrievalMessage}</Alert>
        }

        if ((this.props.view === View.Update || this.props.view === View.Read) && !this.state.plateMap) {
            return <Alert variant={"info"}>No plate map uploaded</Alert>
        }

        const plasmidOptions = this.convertPlasmidListToOptions(this.state.possiblePlasmidList)

        return <>
            <p>Plate Map</p>
            <TanStackPlateMapTable
                columns={this.state.columns}
                data={PlateRow.convertStrToPlateMapRowList(this.state.plateMap)}
                plateMap={this.state.plateMap}
                options={plasmidOptions}
                editable={this.props.view !== View.Read}
                onPlateMapChange={this.handlePlateMapChange}
            />
            {
                this.props.view === View.Update &&
                <Row>
                    <Col md={12}>
                        <Form.Group>
                            <Form.Label>User</Form.Label>
                            <Form.Control type={"text"} disabled value={this.state.user}/>
                        </Form.Group>
                    </Col>
                    <Col md={12}>
                        <Form.Group>
                            <Form.Label>Update note</Form.Label>
                            <Form.Control
                                type={"text"}
                                value={this.state.updateNote}
                                onChange={this.handleUpdateNoteChange}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            }
            {
                this.state.plateMapSubmissionStatus === ApiCallStatus.Error &&
                <Row className={"mt-2"}>
                    <Col md={12}>
                        <Alert className={"mt-3"} variant={"danger"}>
                            {this.state.plateMapSubmissionMessage}
                        </Alert>
                    </Col>
                </Row>
            }
            {
                this.state.plateMapSubmissionStatus === ApiCallStatus.Completed &&
                <Row className={"mt-2"}>
                    <Col md={12}>
                        <Alert variant={"success"}>
                            Plate ID {this.props.genGenPlateMap.plateId} submitted successfully!
                        </Alert>
                    </Col>
                </Row>
            }
            {
                this.props.view !== View.Read &&
                <Button className={"mt-2 float-end"} variant={"primary"}
                        onClick={this.submitPlateMap}
                        disabled={this.state.plateMapSubmissionStatus === ApiCallStatus.Loading}>
                    {this.state.plateMapSubmissionStatus === ApiCallStatus.Loading ? "Submitting ..." : "Submit"}
                </Button>
            }
        </>;
    }
}